import { gql } from '@apollo/client';

import { PERSON_KOMMO_LEAD_FIELDS } from '../fragments/crm';

/**
 * Lightweight queries that back the new detail-route Overview tabs. Each
 * picks the minimum fields the tab actually renders so the cache stays
 * cheap — the heavy popups (StudentPaymentPopup, EditGroupPopup,
 * TeacherEditPopup) keep using their own queries.
 */

export const StudentDetailQuery = gql`
  ${PERSON_KOMMO_LEAD_FIELDS}
  query StudentDetail($id: ID!) {
    student(id: $id) {
      id
      registrationDate
      acquisitionSource
      # ТЗ 0086: лиды Kommo — «откуда пришёл». Роли без доступа к лидам
      # получают пустой список.
      kommoLeads {
        ...PersonKommoLeadFields
      }
      user {
        id
        firstName
        lastName
        middleName
        email
        phone
      }
      # Блок «Группы» и сводка групп в шапке — учебный срез без денег:
      # строка на группу из всех договоров (с расторгнутыми), действующие
      # первыми. Управленцу и методисту (у методиста agreements пусты —
      # договор несёт деньги, решение владельца 07.10.2026).
      educationGroups {
        groupId
        groupName
        courseName
        agreementTerminated
      }
      timeline {
        edges {
          node {
            id
            type
            date
            label
            meta
          }
        }
      }
      accountsPayables {
        id
        amountForeign
        amountByn
        currency
        repaid
        startedAgreement {
          id
          agreementTitle
        }
      }
      agreements {
        id
        agreementTitle
        currency
        budgetAmount
        paidAmount
        debtAmount
        upcomingAmount
        terminated
        terminatedDate
        transferOut
        freeOfCharge
        statePrefinanced
        date
        partialAttendance
        paymentByCompany
        vat
        resident
        promocode
        comment
        legalName
        taxId
        street
        city
        zipCode
        address
        country {
          id
          title
        }
        company {
          id
          name
        }
        group {
          id
          name
          startDate
          course {
            id
            name
          }
        }
        firstLesson {
          id
          name
          lessonNumber
        }
        lastLesson {
          id
          name
          date
          lessonNumber
        }
        pandadocDocument {
          id
          pandadocId
          name
          status
          outcome
          docType
          course
          dateCompleted
          ttsHours
        }
        contractDocument {
          id
          number
          status
          fulfilmentStatus
          completedAt
        }
        payments {
          id
          date
          amountAgreement
          currency
          source
          fakturowniaInvoice {
            id
            pk
            number
            status
            priceGross
            outstanding
            kind
            currency
          }
          contribution {
            id
          }
        }
        contributions(orderByDateFilter: true) {
          edges {
            node {
              id
              date
              amountForeign
              amountByn
              complete
              remainder
              delay
              status
              coveredAmount
              debt
              paidManually
              proformas {
                id
                pk
                number
                status
                paymentTo
                currency
                priceGross
                viewUrl
                isOpenProforma
                vatChild {
                  id
                  number
                }
              }
              invoices {
                id
                pk
                number
                kind
                status
                paid
                outstanding
                netGross
                netPaid
                paymentTo
                currency
                provisionBlockedReason
                contributionMatchSource
                payment {
                  id
                  date
                  amountAgreement
                  currency
                }
              }
              # Документы взноса в чужой валюте: сервер их не учитывает ни в
              # покрытии, ни в статусе — карточка объясняет почему.
              currencyMismatchInvoices {
                id
                number
                currency
                kind
                paid
              }
              payment {
                id
                date
                amountAgreement
                currency
                fakturowniaInvoice {
                  id
                  pk
                  number
                  status
                  priceGross
                  outstanding
                  kind
                  currency
                }
              }
            }
          }
        }
        chargeback {
          id
          currency
          amountForeign
          amountByn
          date
        }
        startAccountsPayable {
          id
          amountForeign
          currency
          repaid
        }
        corrections {
          id
          date
          amountForeign
          amountByn
          currency
          reason
          payment {
            id
          }
        }
      }
    }
  }
`;

/**
 * `$withStaffFields` — `price`, `currency`, `deleteAvailable`, `teachers`
 * закрыты на бэкенде ролями на уровне ПОЛЯ (`GROUP_STAFF_FIELDS_ROLES`):
 * методисту карточка открыта, а эти поля приходили отказом (3–4 ошибки на
 * каждую загрузку). `contributionsSum` вне списка — `null`, без ошибки.
 */
export const GroupDetailQuery = gql`
  query GroupDetail($id: ID!, $withStaffFields: Boolean!) {
    group(groupId: $id) {
      id
      name
      startDate
      price @include(if: $withStaffFields)
      currency @include(if: $withStaffFields)
      studentsCount
      lessonsCount
      passedLessonsCount
      freeLessonsCount
      contributionsSum
      maxStudentsCount
      freePlaces
      statusFinished
      datetimeNextLesson
      deleteAvailable @include(if: $withStaffFields)
      isFree
      comment
      publishedLms
      publishedLmsForTeacher
      publishedLmsForMethodist
      course {
        id
        name
      }
      teachers @include(if: $withStaffFields) {
        id
        fullName
      }
    }
  }
`;

export const TeacherDetailQuery = gql`
  query TeacherDetail($id: ID!) {
    teacher(teacherId: $id) {
      id
      fullName
      accessToLms
      isDismissed
      groupsCount
      rating
      studentRating
      studentRatingCount
      studentComments {
        studentName
        comment
        createdAt
        courseName
        groupName
      }
      internalNote
      interviewRecording
      photo
      description
      user {
        id
        firstName
        lastName
        middleName
        email
        phone
      }
      courses
      coursesCanTeach {
        id
        name
      }
      employmentTypes {
        __typename
      }
      applications {
        edges {
          node {
            id
            title
            lessonsCount
            appointedLessonsCount
            servicePrice
            riaPrice
            currency
            isTerminated
            isCompleted
            teacher {
              id
              fullName
            }
            group {
              id
              name
            }
            employmentType {
              __typename
            }
          }
        }
      }
    }
  }
`;
